import type { DocPage } from '../types';

export const docPages: Record<string, DocPage> = {
  '/pos/transaction': {
    key: '/pos/transaction',
    title: 'Nouvelle Transaction',
    path: '/pos/transaction',
    definition:
      'Écran principal de caisse (POS). Il permet d’enregistrer une vente en associant prestations et/ou produits, un client, les remises éventuelles, puis de finaliser l’encaissement avec un ou plusieurs modes de paiement.',
    utility:
      'C’est le point d’entrée commercial de l’application : toute vente validée ici alimente l’historique, les rapports de caisse, la fidélité client, le stock (produits vendus ou consommés), et peut déclencher des ordres de fabrication ou des paiements en plusieurs selon la configuration.',
    relatedModules: [
      { label: 'Prestations', path: '/services', relation: 'Catalogue des services vendus en caisse (prix, durée, modèle de fabrication).' },
      { label: 'Produits', path: '/inventory/products', relation: 'Articles vendus ou scannés ; décrémentation stock à la validation.' },
      { label: 'Clients', path: '/clients', relation: 'Fiche client, cagnotte, historique ; obligatoire pour certains flux (OF, paiement en plusieurs).' },
      { label: 'Paiement en plusieurs', path: '/payment-plans', relation: 'Payer en plusieurs depuis le panier (acompte 0 possible) au lieu d’un paiement intégral.' },
      { label: 'Ordres de fabrication', path: '/manufacturing/orders', relation: 'Créés automatiquement si une prestation du panier est configurée en mode fabrication.' },
      { label: 'Rapports', path: '/pos/reports', relation: 'Synthèse des encaissements issus des transactions validées.' },
    ],
    whenToUse: [
      'À chaque vente en boutique ou au comptoir.',
      'Lors d’un paiement unique ou fractionné (plusieurs modes de paiement).',
      'Quand il faut lier la vente à un client identifié (fidélité, fabrication, suivi).',
    ],
    keyActions: [
      'Composer le panier (prestations, produits, quantités).',
      'Rechercher ou créer un client ; appliquer promotions et cagnotte.',
      'Répartir le règlement (espèces, mobile money, virement, etc.).',
      'Imprimer ou télécharger le reçu (PDF).',
    ],
    workflow: [
      'Ouvrir la caisse et ajouter les lignes au panier.',
      'Vérifier totaux, remises et client associé.',
      'Choisir le mode de règlement (paiement complet ou payer en plusieurs).',
      'Valider : la vente est enregistrée et les modules liés sont mis à jour.',
    ],
    importantNotes: [
      'Le total des paiements doit être strictement égal au total panier ; sinon la validation est refusée.',
      'Une transaction peut regrouper plusieurs lignes (prestations + produits) sous un même numéro de reçu.',
      'Les prestations « fabrication » exigent un client identifié avant validation.',
    ],
    checks: ['Montants cohérents', 'Client renseigné si requis', 'Stock suffisant pour les produits vendus'],
  },

  '/manufacturing/orders': {
    key: '/manufacturing/orders',
    title: 'Ordres de fabrication',
    path: '/manufacturing/orders',
    definition:
      'Un ordre de fabrication (OF) est un bon de travail de production. Il est créé automatiquement lorsqu’une prestation vendue en caisse est configurée pour nécessiter une fabrication différée : la vente est encaissée tout de suite, mais la consommation des matières premières n’intervient qu’à la clôture de l’OF. Il n’existe pas de création manuelle d’OF : ils naissent toujours d’une transaction caisse.',
    utility:
      'Séparer le moment commercial (encaissement, trace vente) du moment opérationnel (consommation réelle du stock). Cas typique : cabinet de prothèse — le patient paie (ou paie en plusieurs fois), l’équipe fabrique ensuite la prothèse et ne sort du stock les composants qu’une fois la pièce terminée. L’OF garantit la traçabilité client → vente → production → mouvements stock.',
    relatedModules: [
      { label: 'Nouvelle Transaction', path: '/pos/transaction', relation: 'Source unique des OF : à la validation d’une prestation « fabrication », l’OF est créé avec le client, la prestation et le groupe de transaction.' },
      { label: 'Prestations', path: '/services', relation: 'Chaque prestation éligible porte un modèle de fabrication (produits prévus, quantités, site de stock). Sans ce paramétrage, aucun OF n’est généré.' },
      { label: 'Clients', path: '/clients', relation: 'Client obligatoire sur la vente pour les prestations en mode fabrication ; repris sur l’OF pour le suivi.' },
      { label: 'Paiement en plusieurs', path: '/payment-plans', relation: 'Fréquent en prothèse : acompte puis solde en paiement en plusieurs, fabrication pilotée par l’OF, vente tracée via la prestation et le transactionGroupId.' },
      { label: 'Historique Prestations', path: '/pos/prestations-history', relation: 'Retrouver la vente d’origine liée à l’OF (prestationId, transactionGroupId).' },
      { label: 'Mouvements', path: '/inventory/movements', relation: 'À la clôture, sorties stock enregistrées avec lien vers prestationId et transactionGroupId pour audit complet.' },
      { label: 'Produits', path: '/inventory/products', relation: 'Matières consommées lors de la clôture ; stock décrémenté de façon atomique (transaction Firestore).' },
    ],
    whenToUse: [
      'Après une vente de prestation nécessitant une production en atelier/laboratoire.',
      'Pour suivre l’avancement (brouillon, en cours, en attente, terminé, annulé).',
      'Au moment où la pièce est livrée et que les matières doivent être sorties du stock.',
    ],
    keyActions: [
      'Consulter la liste des OF (filtres par statut, client, prestation).',
      'Ouvrir un OF : voir lignes prévues vs consommation réelle, client, prestation liée.',
      'Mettre à jour le statut (en cours, en attente…) et l’assignation.',
      'Clôturer l’OF : saisir les quantités réellement consommées → déduction stock + mouvements tracés.',
    ],
    workflow: [
      '1. En caisse : vendre une prestation configurée « ordre de fabrication » avec un client identifié → l’OF apparaît en statut Brouillon.',
      '2. Atelier : ouvrir l’OF, ajuster les lignes si besoin, faire avancer le statut pendant la production.',
      '3. Clôture : valider les quantités consommées → le stock est déduit en une seule opération atomique et l’OF passe à Terminé.',
      '4. Contrôle : vérifier les mouvements stock et la cohérence avec la vente dans l’historique prestations.',
    ],
    importantNotes: [
      'Pas de création manuelle : l’écran indique que les OF proviennent du paiement en caisse.',
      'Deux modes de consommation stock pour les prestations : immédiate à la vente (produits consommés) OU différée via OF (modèle fabrication) — pas les deux pour la même logique.',
      'La clôture est irréversible côté stock : elle applique les sorties et marque stockApplied.',
    ],
    checks: [
      'Client bien renseigné en caisse avant validation',
      'Stock disponible au site indiqué avant clôture',
      'Quantités réelles cohérentes avec la production',
    ],
  },

  '/pos/prestations-history': {
    key: '/pos/prestations-history',
    title: 'Historique Prestations',
    path: '/pos/prestations-history',
    definition:
      'Journal de toutes les ventes de prestations (services) enregistrées en caisse : montants, dates, clients, employés, promotions et regroupements de transaction.',
    utility:
      'Permet d’auditer le chiffre d’affaires services, de retrouver une vente passée, de justifier un encaissement ou de remonter à l’OF / paiement en plusieurs associé via les identifiants de transaction.',
    relatedModules: [
      { label: 'Nouvelle Transaction', path: '/pos/transaction', relation: 'Origine de chaque ligne d’historique.' },
      { label: 'Ordres de fabrication', path: '/manufacturing/orders', relation: 'Chaque OF référence une prestationId de cet historique.' },
      { label: 'Clients', path: '/clients', relation: 'Historique visible aussi depuis la fiche client.' },
      { label: 'Rapports', path: '/pos/reports', relation: 'Vue agrégée vs détail ligne à ligne ici.' },
    ],
    whenToUse: ['Contrôle caisse', 'Réclamation client', 'Recherche d’une vente pour clôture OF ou suivi commercial'],
    keyActions: ['Filtrer par période, client, employé', 'Ouvrir le détail d’une prestation', 'Identifier transactionGroupId pour ventes groupées'],
    workflow: ['Définir la période', 'Filtrer si besoin', 'Ouvrir la ligne et lire le détail (montant, remises, client, lien transaction)'],
  },

  '/pos/sales-history': {
    key: '/pos/sales-history',
    title: 'Historique Produits',
    path: '/pos/sales-history',
    definition: 'Journal des ventes de produits (marchandises) passées en caisse, distinct des prestations.',
    utility: 'Suivre les ventes article par article, croiser avec les mouvements stock sortants « vente produit », analyser les performances produits.',
    relatedModules: [
      { label: 'Nouvelle Transaction', path: '/pos/transaction', relation: 'Point de saisie des ventes produits.' },
      { label: 'Produits', path: '/inventory/products', relation: 'Fiches articles vendus ; stock mis à jour à la vente.' },
      { label: 'Mouvements', path: '/inventory/movements', relation: 'Trace chaque sortie liée à une vente.' },
      { label: 'Rapports Stock', path: '/inventory/dashboard', relation: 'Vision agrégée rotation et ruptures.' },
    ],
    whenToUse: ['Inventaire commercial', 'Vérification vente d’un SKU', 'Analyse des best-sellers'],
    keyActions: ['Filtrer par date, produit, client', 'Consulter quantités et montants', 'Exporter si autorisé'],
    workflow: ['Choisir période', 'Rechercher produit ou client', 'Contrôler la ligne et rapprocher du stock'],
  },

  '/pos/reports': {
    key: '/pos/reports',
    title: 'Rapports caisse',
    path: '/pos/reports',
    definition: 'Tableaux de bord et exports de synthèse caisse : ventes, modes de paiement, périodes.',
    utility: 'Clôture de journée, contrôle encaissements, reporting direction sans parcourir chaque transaction une par une.',
    relatedModules: [
      { label: 'Nouvelle Transaction', path: '/pos/transaction', relation: 'Alimente les données agrégées.' },
      { label: 'Historique Prestations / Produits', path: '/pos/prestations-history', relation: 'Détail transactionnel vs synthèse ici.' },
      { label: 'Comptabilité', path: '/accounting/dashboard', relation: 'Les écritures comptables s’appuient sur les flux de ventes et dépenses.' },
    ],
    whenToUse: ['Fin de journée / fin de mois', 'Rapprochement caisse banque', 'Reporting management'],
    keyActions: ['Sélectionner période', 'Lire totaux par type et par paiement', 'Exporter Excel/PDF selon droits'],
    workflow: ['Choisir la plage de dates', 'Analyser les indicateurs', 'Exporter ou partager au comptable'],
  },

  '/marketing': {
    key: '/marketing',
    title: 'Marketing',
    path: '/marketing',
    definition: 'Module de campagnes marketing : ciblage de segments clients, envois (SMS, e-mail selon intégration), promotions liées à la caisse.',
    utility: 'Fidéliser et réactiver la clientèle, mesurer l’impact des campagnes, croiser promotions marketing et remises POS.',
    relatedModules: [
      { label: 'Clients', path: '/clients', relation: 'Base de ciblage (segments, consentements).' },
      { label: 'Nouvelle Transaction', path: '/pos/transaction', relation: 'Application des promotions en caisse.' },
      { label: 'Messages Clients', path: '/messages-clients', relation: 'Relances individuelles post-achat vs campagnes de masse ici.' },
    ],
    whenToUse: ['Lancement promo saisonnière', 'Relance clients inactifs', 'Communication événement'],
    keyActions: ['Créer campagne et segment', 'Planifier ou envoyer', 'Suivre statuts et statistiques'],
    workflow: ['Définir objectif et cible', 'Rédiger message / promo', 'Lancer et monitorer'],
  },

  '/messages-clients': {
    key: '/messages-clients',
    title: 'Messages Clients',
    path: '/messages-clients',
    definition: 'File de relances automatiques ou semi-automatiques après passage en caisse (suivi personnalisé, souvent WhatsApp).',
    utility: 'Humaniser le suivi post-vente sans ressaisir les coordonnées : chaque message est lié à une transaction récente.',
    relatedModules: [
      { label: 'Nouvelle Transaction', path: '/pos/transaction', relation: 'Chaque vente peut générer une entrée dans la file.' },
      { label: 'Clients', path: '/clients', relation: 'Numéro et opt-out client conditionnent l’envoi.' },
      { label: 'Marketing', path: '/marketing', relation: 'Campagnes massives vs messages unitaires ici.' },
    ],
    whenToUse: ['Après les ventes du jour', 'Pour remercier ou demander un avis', 'Quand la file affiche des messages « prêts »'],
    keyActions: ['Traiter la file', 'Envoyer via WhatsApp Web', 'Marquer envoyé / ignoré'],
    workflow: ['Ouvrir Messages Clients', 'Traiter les lignes prêtes', 'Respecter opt-out et délais anti-spam'],
    importantNotes: ['Sans numéro valide ou avec opt-out, le message est ignoré automatiquement.'],
  },

  '/appointments': {
    key: '/appointments',
    title: 'Calendrier',
    path: '/appointments',
    definition: 'Agenda des rendez-vous : créneaux, prestations, clients, ressources (employés, cabines…).',
    utility: 'Organiser l’activité, éviter les doubles réservations, lier planning et ventes ultérieures en caisse.',
    relatedModules: [
      { label: 'Demandes RDV', path: '/appointment-requests', relation: 'Demandes web converties en RDV confirmés.' },
      { label: 'Prestations', path: '/services', relation: 'Durée et type de service planifiés.' },
      { label: 'Clients', path: '/clients', relation: 'Fiche client associée au créneau.' },
    ],
    whenToUse: ['Prise de RDV interne', 'Visualisation charge équipe', 'Modification / annulation'],
    keyActions: ['Créer, déplacer, annuler RDV', 'Associer prestation et client', 'Voir vue jour/semaine'],
    workflow: ['Sélectionner créneau', 'Renseigner client et prestation', 'Enregistrer'],
  },

  '/appointment-requests': {
    key: '/appointment-requests',
    title: 'Demandes RDV',
    path: '/appointment-requests',
    definition: 'File des demandes de rendez-vous reçues depuis les pages publiques ou formulaires clients, en attente de validation.',
    utility: 'Ne pas perdre les demandes en ligne : transformer une demande en RDV confirmé dans le calendrier.',
    relatedModules: [
      { label: 'Calendrier', path: '/appointments', relation: 'Destination après acceptation.' },
      { label: 'Clients', path: '/clients', relation: 'Création ou rattachement fiche client.' },
    ],
    whenToUse: ['Badge ou notification de nouvelles demandes', 'Traitement quotidien des leads RDV'],
    keyActions: ['Accepter / refuser', 'Convertir en RDV', 'Contacter le client si besoin'],
    workflow: ['Ouvrir la demande', 'Vérifier disponibilité', 'Accepter → RDV créé dans le calendrier'],
  },

  '/clients': {
    key: '/clients',
    title: 'Clients',
    path: '/clients',
    definition: 'Répertoire central des clients : identité, coordonnées, historique achats, fidélité, rendez-vous, paiements en plusieurs.',
    utility: 'Single source of truth client ; indispensable pour OF, paiements en plusieurs, marketing ciblé et suivi commercial.',
    relatedModules: [
      { label: 'Nouvelle Transaction', path: '/pos/transaction', relation: 'Chaque vente peut être rattachée à une fiche.' },
      { label: 'Paiement en plusieurs', path: '/payment-plans', relation: 'Soldes clients et versements.' },
      { label: 'Ordres de fabrication', path: '/manufacturing/orders', relation: 'OF toujours liés à un client identifié.' },
      { label: 'Calendrier', path: '/appointments', relation: 'RDV historiques et à venir.' },
    ],
    whenToUse: ['Création fiche', 'Consultation historique', 'Mise à jour coordonnées / consentements'],
    keyActions: ['CRUD fiches', 'Voir onglets ventes, RDV, fidélité', 'Recherche multi-critères'],
    workflow: ['Rechercher ou créer', 'Compléter la fiche', 'Exploiter les onglets métier'],
  },

  '/payment-plans': {
    key: '/payment-plans',
    title: 'Paiement en plusieurs',
    path: '/payment-plans',
    definition: 'Soldes clients : une vente peut être payée plus tard (acompte 0 possible), avec suivi des versements et retards.',
    utility: 'Permettre un acompte (ou rien maintenant) puis le solde plus tard, tout en traçant la vente dès le départ. Très utile avec les OF en prothèse.',
    relatedModules: [
      { label: 'Nouvelle Transaction', path: '/pos/transaction', relation: 'Paiement en plusieurs depuis le panier (alternative au paiement intégral).' },
      { label: 'Clients', path: '/clients', relation: 'Toujours rattaché à un client.' },
      { label: 'Ordres de fabrication', path: '/manufacturing/orders', relation: 'Fabrication peut démarrer pendant que le client paie encore les échéances.' },
      { label: 'Historique Prestations', path: '/pos/prestations-history', relation: 'Vente d’origine et reçus par versement.' },
    ],
    whenToUse: ['Vente à crédit interne', 'Acomptes sur devis', 'Paiement étalé sur plusieurs mois'],
    keyActions: ['Lister les paiements actifs / soldés', 'Enregistrer un versement', 'Voir échéances et retards'],
    workflow: ['Payer en plusieurs en caisse', 'Suivre dans Paiement en plusieurs', 'Enregistrer chaque versement au fil du temps'],
    importantNotes: ['La vente est tracée dès la création ; les versements mettent à jour le solde restant.'],
  },

  '/referents': {
    key: '/referents',
    title: 'Mes Référents',
    path: '/referents',
    definition: 'Gestion des prescripteurs / partenaires (ex. médecins) qui orientent des clients, avec quota ou commission.',
    utility: 'Mesurer l’apport des partenaires, calculer les quotas mensuels, justifier les reversements.',
    relatedModules: [
      { label: 'Nouvelle Transaction', path: '/pos/transaction', relation: 'Le référent peut être choisi lors de la vente.' },
      { label: 'Clients', path: '/clients', relation: 'Clients orientés par un référent.' },
    ],
    whenToUse: ['Réseau prescripteurs', 'Fin de mois quota', 'Suivi partenariat médical'],
    keyActions: ['Gérer fiches référents', 'Consulter ventes liées', 'Analyser quotas'],
    workflow: ['Paramétrer référent', 'Sélectionner en caisse', 'Contrôler le détail mensuel'],
  },

  '/dashboard': {
    key: '/dashboard',
    title: 'Tableau de bord',
    path: '/dashboard',
    definition: 'Vue synthétique de l’activité : KPI, graphiques, alertes selon modules activés et profil utilisateur.',
    utility: 'Pilotage rapide sans entrer dans chaque module ; repérer tendances et anomalies.',
    relatedModules: [
      { label: 'Rapports caisse', path: '/pos/reports', relation: 'Détail encaissements.' },
      { label: 'Rapports Stock', path: '/inventory/dashboard', relation: 'Alertes ruptures.' },
      { label: 'Comptabilité', path: '/accounting/dashboard', relation: 'Vision financière comptable.' },
    ],
    whenToUse: ['Brief matinal', 'Réunion direction', 'Surveillance alertes'],
    keyActions: ['Choisir période', 'Lire KPI', 'Naviguer vers le module concerné'],
    workflow: ['Ouvrir le tableau de bord', 'Interpréter les indicateurs', 'Approfondir si anomalie'],
  },

  '/inventory/products': {
    key: '/inventory/products',
    title: 'Produits',
    path: '/inventory/products',
    definition: 'Catalogue articles stockables : référence, prix, stock courant, seuils, code-barres, sites multi-lieux.',
    utility: 'Référentiel pour ventes caisse, livraisons fournisseurs, OF, inventaires ; base de toute traçabilité stock.',
    relatedModules: [
      { label: 'Mouvements', path: '/inventory/movements', relation: 'Historique entrées/sorties par produit.' },
      { label: 'Nouvelle Transaction', path: '/pos/transaction', relation: 'Vente et scan code-barres.' },
      { label: 'Ordres de fabrication', path: '/manufacturing/orders', relation: 'Consommation matière à la clôture.' },
      { label: 'Livraisons', path: '/suppliers/deliveries', relation: 'Entrées stock fournisseur.' },
    ],
    whenToUse: ['Création article', 'Mise à jour prix/stock', 'Impression étiquettes code-barres'],
    keyActions: ['CRUD produits', 'Gérer code-barres (interne ou EAN)', 'Imprimer / PDF étiquettes avec ou sans prix'],
    workflow: ['Créer la fiche complète', 'Définir seuil alerte', 'Utiliser en caisse ou livraison'],
    importantNotes: ['Les étiquettes code-barres sont disponibles en impression directe ou PDF.'],
  },

  '/inventory/categories': {
    key: '/inventory/categories',
    title: 'Catégories de produits',
    path: '/inventory/categories',
    definition: 'Classification du catalogue produits par familles (cosmétiques, consommables, etc.).',
    utility: 'Faciliter recherche caisse, filtres rapports et organisation du stock.',
    relatedModules: [{ label: 'Produits', path: '/inventory/products', relation: 'Chaque produit appartient à une catégorie.' }],
    whenToUse: ['Structuration initiale catalogue', 'Ajout nouvelle gamme'],
    keyActions: ['Créer / renommer catégories', 'Classer les produits'],
    workflow: ['Définir l’arborescence', 'Affecter les produits'],
  },

  '/inventory/movements': {
    key: '/inventory/movements',
    title: 'Mouvements de stock',
    path: '/inventory/movements',
    definition: 'Journal chronologique de toutes les entrées et sorties de stock avec motif, auteur, liens métier (vente, prestation, OF, livraison…).',
    utility: 'Audit et traçabilité : comprendre pourquoi le stock a bougé et rapprocher avec la vente ou l’OF source.',
    relatedModules: [
      { label: 'Produits', path: '/inventory/products', relation: 'Stock courant mis à jour par chaque mouvement.' },
      { label: 'Ordres de fabrication', path: '/manufacturing/orders', relation: 'Sorties « clôture OF » avec prestationId.' },
      { label: 'Livraisons', path: '/suppliers/deliveries', relation: 'Entrées réception fournisseur.' },
      { label: 'Historique Produits', path: '/pos/sales-history', relation: 'Sorties vente caisse.' },
    ],
    whenToUse: ['Écart inventaire', 'Contrôle clôture OF', 'Audit mensuel'],
    keyActions: ['Filtrer par produit, type, période', 'Lire motif et références liées'],
    workflow: ['Filtrer', 'Identifier le mouvement', 'Remonter au document source (vente, OF, livraison)'],
  },

  '/inventory/external-dispatches': {
    key: '/inventory/external-dispatches',
    title: 'Bons externes',
    path: '/inventory/external-dispatches',
    definition: 'Documents de sortie de stock vers une destination externe (autre site, entrepôt tiers, partenaire).',
    utility: 'Tracer les envois hors vente directe ; éviter les sorties « non expliquées » en stock.',
    relatedModules: [
      { label: 'Destinations externes', path: '/inventory/external-destinations', relation: 'Référentiel des lieux de destination.' },
      { label: 'Mouvements', path: '/inventory/movements', relation: 'Chaque bon validé génère des sorties.' },
    ],
    whenToUse: ['Transfert vers filiale', 'Envoi atelier externe', 'Sortie exceptionnelle tracée'],
    keyActions: ['Créer bon', 'Saisir lignes', 'Valider et suivre statut'],
    workflow: ['Choisir destination', 'Ajouter produits/quantités', 'Valider'],
  },

  '/inventory/external-destinations': {
    key: '/inventory/external-destinations',
    title: 'Destinations externes',
    path: '/inventory/external-destinations',
    definition: 'Paramétrage des lieux recevant des sorties via bons externes.',
    utility: 'Standardiser les destinations et accélérer la saisie des bons.',
    relatedModules: [{ label: 'Bons externes', path: '/inventory/external-dispatches', relation: 'Utilise ces destinations.' }],
    whenToUse: ['Premier paramétrage multi-sites', 'Ajout nouveau partenaire logistique'],
    keyActions: ['Créer / modifier destinations'],
    workflow: ['Créer la destination', 'Utiliser dans les bons'],
  },

  '/inventory/audit': {
    key: '/inventory/audit',
    title: 'Inventaires physiques',
    path: '/inventory/audit',
    definition: 'Sessions de comptage terrain : comparer stock théorique (système) et stock réel (compté).',
    utility: 'Régulariser les écarts, clôturer une période stock, préparer bilan inventaire.',
    relatedModules: [
      { label: 'Produits', path: '/inventory/products', relation: 'Stock ajusté après clôture session.' },
      { label: 'Mouvements', path: '/inventory/movements', relation: 'Mouvements d’ajustement générés.' },
    ],
    whenToUse: ['Inventaire annuel / tournant', 'Contrôle après incident'],
    keyActions: ['Créer session', 'Saisir comptages (y compris mobile)', 'Clôturer et appliquer écarts'],
    workflow: ['Planifier session', 'Compter', 'Analyser écarts', 'Clôturer'],
  },

  '/inventory/dashboard': {
    key: '/inventory/dashboard',
    title: 'Rapports Stock',
    path: '/inventory/dashboard',
    definition: 'Indicateurs stock : ruptures, seuils bas, rotation, vue managériale inventaire.',
    utility: 'Anticiper réapprovisionnements avant impact caisse ou production (OF bloqués par manque matière).',
    relatedModules: [
      { label: 'Produits', path: '/inventory/products', relation: 'Détail par article.' },
      { label: 'Livraisons', path: '/suppliers/deliveries', relation: 'Action corrective réappro.' },
      { label: 'Ordres de fabrication', path: '/manufacturing/orders', relation: 'Risque blocage production si rupture.' },
    ],
    whenToUse: ['Réunion appro', 'Alertes menu Stock (badge)'],
    keyActions: ['Lire KPI', 'Identifier articles critiques', 'Planifier commandes'],
    workflow: ['Consulter dashboard', 'Lister ruptures', 'Déclencher livraison ou transfert'],
  },

  '/suppliers': {
    key: '/suppliers',
    title: 'Fournisseurs',
    path: '/suppliers',
    definition: 'Annuaire des fournisseurs et partenaires d’achat.',
    utility: 'Centraliser contacts et historique d’approvisionnement ; prérequis aux livraisons.',
    relatedModules: [
      { label: 'Livraisons', path: '/suppliers/deliveries', relation: 'Chaque réception est liée à un fournisseur.' },
      { label: 'Dépenses', path: '/expenses', relation: 'Factures fournisseurs saisies en dépenses.' },
    ],
    whenToUse: ['Nouveau fournisseur', 'Mise à jour coordonnées'],
    keyActions: ['CRUD fournisseurs', 'Consulter historique achats'],
    workflow: ['Créer fiche', 'Enregistrer livraisons', 'Suivre dépenses associées'],
  },

  '/suppliers/deliveries': {
    key: '/suppliers/deliveries',
    title: 'Livraisons & transferts',
    path: '/suppliers/deliveries',
    definition: 'Réceptions fournisseurs et transferts de stock entre sites (boutique, magasin, entrepôt).',
    utility: 'Augmenter le stock de façon tracée ; réapprovisionner la boutique depuis le magasin ou un fournisseur.',
    relatedModules: [
      { label: 'Fournisseurs', path: '/suppliers', relation: 'Source pour réceptions achat.' },
      { label: 'Produits', path: '/inventory/products', relation: 'Stock incrémenté à la validation.' },
      { label: 'Mouvements', path: '/inventory/movements', relation: 'Entrées / transferts enregistrés.' },
    ],
    whenToUse: ['Réception marchandise', 'Ravitailler la boutique', 'Transfert inter-sites'],
    keyActions: [
      'Recevoir des produits des fournisseurs',
      'Envoyer des produits à la boutique (transfert)',
      'Valider quantités et impact stock',
    ],
    workflow: ['Choisir type (réception ou transfert)', 'Saisir lignes', 'Valider'],
    importantNotes: [
      'Libellés adaptés selon présence d’un site « magasin » : envoi boutique vs transfert générique entre lieux.',
    ],
  },

  '/expenses': {
    key: '/expenses',
    title: 'Dépenses',
    path: '/expenses',
    definition: 'Saisie des charges de l’établissement (loyer, électricité, achats courants…) par catégorie et date.',
    utility: 'Suivi trésorerie et alimentation comptabilité ; distinct des notes de frais salariés.',
    relatedModules: [
      { label: 'Notes de frais', path: '/expense-notes', relation: 'Workflow validation avant création dépense.' },
      { label: 'Paies', path: '/payrolls', relation: 'Génère des dépenses salaires à la validation paie.' },
      { label: 'Comptabilité', path: '/accounting/journals', relation: 'Écritures issues des dépenses.' },
    ],
    whenToUse: ['Saisie facture', 'Charge récurrente', 'Contrôle mensuel'],
    keyActions: ['Créer dépense', 'Joindre justificatif', 'Filtrer par catégorie/période'],
    workflow: ['Saisir montant et date métier', 'Classer', 'Enregistrer'],
  },

  '/expense-notes': {
    key: '/expense-notes',
    title: 'Notes de frais',
    path: '/expense-notes',
    definition: 'Workflow collaboratif : un employé soumet une note (montant, date, justificatif), un validateur approuve ou rejette, puis une dépense est créée et un remboursement est ajouté à la paie.',
    utility: 'Encadrer les remboursements et frais professionnels avec validation et pièces jointes.',
    relatedModules: [
      { label: 'Dépenses', path: '/expenses', relation: 'Une dépense est créée à l’approbation, datée au jour de la note (pas au jour de validation).' },
      { label: 'Paies', path: '/payrolls', relation: 'Ajustement de remboursement sur la paie ; la dépense salaire n’inclut pas ce montant (déjà compté via la note).' },
      { label: 'Employés', path: '/employees', relation: 'Auteur et validateur identifiés.' },
    ],
    whenToUse: ['Frais déplacement', 'Achat urgent pour le compte de l’entreprise'],
    keyActions: ['Soumettre note', 'Modifier tant que non validée (créateur)', 'Approuver / rejeter'],
    workflow: ['Créer note + PJ', 'Soumettre', 'Validation → dépense + ajustement paie'],
    importantNotes: [
      'Le créateur peut modifier sa note tant qu’elle n’est pas validée.',
      'La dépense générée reprend la date de la note, pas la date de validation.',
      'Le remboursement augmente le net payé à l’employé, mais n’est pas recompté dans la dépense « Salaire » à la validation de paie (évite le double comptage).',
      'Si la paie du mois de la note est déjà validée ou payée, le remboursement est reporté automatiquement sur le prochain mois ouvert (avec confirmation à l’écran).',
    ],
  },

  '/accounting/dashboard': {
    key: '/accounting/dashboard',
    title: 'Comptabilité — Tableau de bord',
    path: '/accounting/dashboard',
    definition: 'Synthèse comptable : soldes, indicateurs, entrées vers le détail des journaux.',
    utility: 'Vision financière structurée pour le responsable comptable ou la direction.',
    relatedModules: [
      { label: 'Journaux comptables', path: '/accounting/journals', relation: 'Détail des écritures.' },
      { label: 'Dépenses', path: '/expenses', relation: 'Source des charges.' },
      { label: 'Rapports caisse', path: '/pos/reports', relation: 'Source des produits.' },
    ],
    whenToUse: ['Suivi mensuel', 'Préparation bilan'],
    keyActions: ['Lire indicateurs', 'Naviguer vers journaux'],
    workflow: ['Choisir période', 'Analyser', 'Approfondir'],
  },

  '/accounting/journals': {
    key: '/accounting/journals',
    title: 'Journaux comptables',
    path: '/accounting/journals',
    definition: 'Liste des écritures comptables générées (ventes, achats, dépenses, paies…).',
    utility: 'Contrôle comptable détaillé, export, préparation déclarations.',
    relatedModules: [
      { label: 'Déclarations TVA', path: '/accounting/tax-returns', relation: 'Consolidation fiscale.' },
      { label: 'Dépenses', path: '/expenses', relation: 'Origine des écritures de charge.' },
    ],
    whenToUse: ['Revue comptable', 'Export expert-comptable'],
    keyActions: ['Filtrer écritures', 'Contrôler montants', 'Exporter'],
    workflow: ['Filtrer période/type', 'Vérifier', 'Exporter'],
  },

  '/accounting/tax-returns': {
    key: '/accounting/tax-returns',
    title: 'Déclarations TVA',
    path: '/accounting/tax-returns',
    definition: 'Préparation et suivi des déclarations de TVA par période fiscale.',
    utility: 'Sécuriser la conformité fiscale et rapprocher TVA collectée / déductible.',
    relatedModules: [
      { label: 'Journaux comptables', path: '/accounting/journals', relation: 'Base de calcul.' },
      { label: 'Rapports caisse', path: '/pos/reports', relation: 'Ventes HT/TVA.' },
    ],
    whenToUse: ['Échéance fiscale', 'Contrôle avant télédéclaration'],
    keyActions: ['Sélectionner période', 'Vérifier bases', 'Valider statut déclaration'],
    workflow: ['Ouvrir période', 'Contrôler montants', 'Finaliser'],
  },

  '/services': {
    key: '/services',
    title: 'Prestations',
    path: '/services',
    definition: 'Catalogue des services vendus en caisse : libellé, prix, durée (RDV), consommation produits ou modèle de fabrication.',
    utility: 'Définir ce qui est vendable au POS et comment cela impacte stock et production (OF).',
    relatedModules: [
      { label: 'Nouvelle Transaction', path: '/pos/transaction', relation: 'Les prestations apparaissent dans le panier.' },
      { label: 'Ordres de fabrication', path: '/manufacturing/orders', relation: 'Si manufacturingTemplate renseigné → OF auto à la vente.' },
      { label: 'Calendrier', path: '/appointments', relation: 'Durée des prestations pour le planning.' },
      { label: 'Catégories de prestations', path: '/services/categories', relation: 'Organisation du catalogue.' },
    ],
    whenToUse: ['Nouveau service', 'Tarification', 'Paramétrage fabrication prothèse / atelier'],
    keyActions: [
      'Créer / modifier prestation',
      'Définir prix et durée',
      'Configurer produits consommés immédiatement OU modèle fabrication (OF)',
    ],
    workflow: ['Créer la prestation', 'Choisir mode stock (immédiat vs OF)', 'Tester en caisse'],
    importantNotes: [
      'Mode fabrication : renseigner defaultLines (produits + quantités + site). Client obligatoire en caisse.',
    ],
  },

  '/services/categories': {
    key: '/services/categories',
    title: 'Catégories de prestations',
    path: '/services/categories',
    definition: 'Regroupement thématique des prestations (soins, prothèse, consultation…).',
    utility: 'Navigation caisse et reporting plus lisibles.',
    relatedModules: [{ label: 'Prestations', path: '/services', relation: 'Chaque prestation a une catégorie.' }],
    whenToUse: ['Organisation catalogue', 'Refonte offre'],
    keyActions: ['CRUD catégories', 'Affecter prestations'],
    workflow: ['Créer catégories', 'Classer les prestations'],
  },

  '/employees': {
    key: '/employees',
    title: 'Employés',
    path: '/employees',
    definition: 'Fiches collaborateurs : identité, poste, lien utilisateur application, profil de rôle.',
    utility: 'Associer les actions (ventes, OF, rapports) à une personne ; base des paies et objectifs.',
    relatedModules: [
      { label: 'Profils de rôle', path: '/settings/role-profiles', relation: 'Détermine les menus visibles.' },
      { label: 'Paies', path: '/payrolls', relation: 'Bulletins et salaires.' },
      { label: 'Indicateurs de performance', path: '/performance-indicators', relation: 'Objectifs par employé.' },
    ],
    whenToUse: ['Embauche', 'Changement de poste', 'Désactivation départ'],
    keyActions: ['CRUD employés', 'Lier compte utilisateur', 'Affecter profil de rôle'],
    workflow: ['Créer fiche', 'Associer profil', 'Activer accès'],
  },

  '/settings/role-profiles': {
    key: '/settings/role-profiles',
    title: 'Profils de rôle',
    path: '/settings/role-profiles',
    definition: 'Matrices de permissions : quels modules et actions chaque profil peut voir (caisse, stock, paie, validation notes de frais…).',
    utility: 'Sécuriser l’accès sans créer un compte par menu ; adapter l’app au métier de chaque utilisateur.',
    relatedModules: [
      { label: 'Employés', path: '/employees', relation: 'Chaque employé reçoit un profil.' },
      { label: 'Nouvelle Transaction', path: '/pos/transaction', relation: 'Les permissions déterminent ce qui est visible en caisse.' },
    ],
    whenToUse: ['Nouveau poste', 'Restriction caisse seule', 'Délégation validation'],
    keyActions: ['Créer / dupliquer profil', 'Cocher permissions', 'Assigner aux employés'],
    workflow: ['Définir le rôle métier', 'Configurer permissions', 'Tester avec un compte test'],
  },

  '/payrolls': {
    key: '/payrolls',
    title: 'Paies',
    path: '/payrolls',
    definition: 'Préparation, validation et suivi des paies mensuelles par employé.',
    utility: 'Automatiser le cycle salarial et créer les dépenses de masse salariale au bon moment comptable.',
    relatedModules: [
      { label: 'Employés', path: '/employees', relation: 'Salariés concernés.' },
      { label: 'Dépenses', path: '/expenses', relation: 'Dépenses salaires créées à la validation.' },
      { label: 'Comptabilité', path: '/accounting/journals', relation: 'Écritures de paie.' },
    ],
    whenToUse: ['Fin de mois', 'Validation après contrôle RH'],
    keyActions: ['Générer paie du mois', 'Valider', 'Marquer payé'],
    workflow: ['Sélectionner mois', 'Contrôler lignes', 'Valider'],
    importantNotes: [
      'À la validation, la dépense salaire est datée au dernier jour du mois de paie (pas au jour de validation).',
    ],
  },

  '/performance-indicators': {
    key: '/performance-indicators',
    title: 'Indicateurs de performance',
    path: '/performance-indicators',
    definition: 'Objectifs chiffrés (CA, volumes…) par employé ou équipe sur une période.',
    utility: 'Piloter la performance commerciale et le suivi managérial.',
    relatedModules: [
      { label: 'Employés', path: '/employees', relation: 'Porteurs des objectifs.' },
      { label: 'Rapports caisse', path: '/pos/reports', relation: 'Réalisations vs cibles.' },
    ],
    whenToUse: ['Fixation objectifs mensuels', 'Entretien performance'],
    keyActions: ['Créer feuille d’objectifs', 'Suivre avancement', 'Clôturer période'],
    workflow: ['Définir cibles', 'Suivre en cours de mois', 'Analyser écarts'],
  },

  '/activity/reports': {
    key: '/activity/reports',
    title: "Rapports d'activité",
    path: '/activity/reports',
    definition: 'Comptes-rendus d’activité commerciale ou opérationnelle (actions, volumes, commentaires), avec workflow brouillon → validation.',
    utility: 'Remonter l’activité terrain de façon structurée ; pièces jointes Excel/PDF pour justificatifs.',
    relatedModules: [
      { label: 'Opportunités', path: '/activity/opportunities', relation: 'Actions peuvent alimenter le pipeline.' },
      { label: 'Configuration pipelines', path: '/settings/pipeline', relation: 'Étapes commerciales liées.' },
    ],
    whenToUse: ['Reporting hebdo commercial', 'Suivi actions terrain'],
    keyActions: ['Rédiger rapport', 'Brouillon autosave (mobile)', 'Valider', 'Joindre 1 Excel + 1 PDF max'],
    workflow: ['Saisir activités', 'Vérifier brouillon enregistré', 'Soumettre à validation'],
    importantNotes: ['Brouillon sauvegardé automatiquement en local pour éviter perte de saisie (appels, etc.).'],
  },

  '/activity/opportunities': {
    key: '/activity/opportunities',
    title: 'Opportunités',
    path: '/activity/opportunities',
    definition: 'Pipeline commercial type kanban : prospects et affaires par étape, montants, probabilités.',
    utility: 'Visualiser le portefeuille commercial et prioriser les relances.',
    relatedModules: [
      { label: 'Configuration pipelines', path: '/settings/pipeline', relation: 'Colonnes du kanban.' },
      { label: "Rapports d'activité", path: '/activity/reports', relation: 'Alimentation depuis le terrain.' },
      { label: 'Clients', path: '/clients', relation: 'Conversion prospect → client.' },
    ],
    whenToUse: ['Suivi commercial B2B', 'Pipeline de devis'],
    keyActions: ['Créer opportunité', 'Déplacer entre étapes', 'Clôturer gagnée/perdue'],
    workflow: ['Créer fiche opportunité', 'Faire avancer', 'Clôturer'],
  },

  '/settings/pipeline': {
    key: '/settings/pipeline',
    title: 'Configuration des pipelines',
    path: '/settings/pipeline',
    definition: 'Paramétrage des étapes du funnel commercial (nom, ordre) utilisées dans Opportunités.',
    utility: 'Adapter le CRM à la terminologie métier de l’entreprise.',
    relatedModules: [{ label: 'Opportunités', path: '/activity/opportunities', relation: 'Consomme ces étapes.' }],
    whenToUse: ['Mise en place CRM', 'Renommage étapes métier'],
    keyActions: ['Ajouter / réordonner étapes', 'Renommer'],
    workflow: ['Définir étapes', 'Tester sur une opportunité', 'Ajuster'],
  },

  '/mon-paiement': {
    key: '/mon-paiement',
    title: 'Paiement licence',
    path: '/mon-paiement',
    definition:
      'Espace client pour consulter le contrat Nauvatek et régler les échéances de licence déjà créées par le commercial (CSM / Super Admin). Le paiement en ligne passe exclusivement par des Cloud Functions (aucune lib provider dans le navigateur).',
    utility:
      'Permettre au responsable d’établissement de régulariser sa licence sans attendre une saisie manuelle côté admin, tout en gardant le contrôle des montants côté Nauvatek.',
    relatedModules: [
      {
        label: 'Mon compte',
        path: '/mon-compte',
        relation: 'Accessible depuis le même menu avatar ; droits utilisateur.',
      },
      {
        label: 'Profils de rôle',
        path: '/settings/role-profiles',
        relation: 'Permission canPayLicence requise pour voir / utiliser cette page.',
      },
    ],
    whenToUse: [
      'Retard de paiement ou échéance proche (bandeau header)',
      'Frais en attente (installation, etc.) créés par le commercial',
      'Consultation de l’historique des règlements licence',
    ],
    keyActions: [
      'Consulter le statut de couverture et le contrat',
      'Sélectionner une ou plusieurs échéances pending',
      'Lancer le paiement (redirect provider via Cloud Function)',
      'Relire l’historique des paiements',
    ],
    workflow: [
      'Le CSM crée des paiements « en attente » sur la fiche contrat Super Admin.',
      'Un utilisateur avec le droit « Payer la licence » ouvre Paiement (menu avatar).',
      'Il sélectionne les échéances à régler puis clique Payer.',
      'La Cloud Function crée une session provider et redirige vers le checkout.',
      'Le webhook provider marque les échéances comme payées (idempotent) et renseigne la date de paiement effective.',
    ],
    importantNotes: [
      'Feature business licencePayment : active par défaut pour tous les établissements.',
      'Permission canPayLicence : à activer sur le profil de rôle (les anciens profils n’ont pas ce droit tant qu’on ne les met pas à jour).',
      'Une échéance en attente n’a pas de date de paiement ; la date est posée seulement au statut payé (admin manuel ou webhook CF).',
      'Ne confondre pas avec les paiements en plusieurs clients salon (/payment-plans) ni avec Mobile Money en caisse.',
      'Documentation technique développeur : docs/module-paiement-licence.md — code provider dans functions/src/licencePayment.ts.',
    ],
    checks: [
      'Feature licencePayment non désactivée sur le business',
      'Profil de rôle avec canPayLicence',
      'Au moins une échéance businessContractPayments en status pending',
      'Cloud Functions déployées (sinon message « bientôt disponible »)',
    ],
  },
};

export const defaultDocPageKey = '/pos/transaction';
